import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';

const CATEGORIE_ORDER = ['JEUNESSE_PRIORITAIRE', 'ADULTE_STANDARD', 'FONCTIONNAIRE_EMPLOYE'] as const;

const CATEGORIE_LABELS: Record<string, string> = {
  JEUNESSE_PRIORITAIRE: 'Jeunesse prioritaire',
  ADULTE_STANDARD: 'Adulte standard',
  FONCTIONNAIRE_EMPLOYE: 'Fonctionnaire / Employé',
};

const TYPE_FRAIS_ORDER = ['INSCRIPTION', 'PARTICIPATION', 'BREVET'] as const;

const TYPE_FRAIS_LABELS: Record<string, string> = {
  INSCRIPTION: "Frais d'inscription",
  PARTICIPATION: 'Frais de participation',
  BREVET: 'Frais de brevet',
};

const tarifInputClass =
  'tarif-field__input w-full min-w-0 rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm tabular-nums text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

type TarifRow = {
  id: string;
  categorie: string;
  typeFrais: string;
  montantCdf?: number | string;
  referenceUsd?: number | string | null;
  version?: number;
  actif?: boolean;
};

type FormateurTarif = {
  id: string;
  montantHoraireCdf?: number | string;
  primeTransportJourCdf?: number | string;
  referenceUsdHoraire?: number | string | null;
  referenceUsdTransport?: number | string | null;
};

type FormationDraft = {
  montantCdf: string;
  referenceUsd: string;
};

type FormateurDraft = {
  montantHoraireCdf: string;
  primeTransportJourCdf: string;
  referenceUsdHoraire: string;
  referenceUsdTransport: string;
};

type ParrainageRegle = {
  id: string;
  typeProfilParrain: string;
  typeProfilLabel: string;
  seuilFilleuls: number;
  tauxCommissionPct: number;
  actif: boolean;
};

type ParrainageSettings = {
  bonusInscriptionCdf: number;
  regles: ParrainageRegle[];
};

type RegleDraft = {
  seuilFilleuls: string;
  tauxCommissionPct: string;
};

type Props = {
  items: TarifRow[];
  formateur: FormateurTarif | null;
  parrainage: ParrainageSettings;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  toastMessage: string | null;
  toastVariant: 'success' | 'error';
  onDismissToast: () => void;
  onRefresh: () => void;
  onSaveFormation: (id: string, payload: { montantCdf: number; referenceUsd: number | null }) => Promise<void>;
  onSaveFormateur: (
    id: string,
    payload: {
      montantHoraireCdf: number;
      primeTransportJourCdf: number;
      referenceUsdHoraire: number | null;
      referenceUsdTransport: number | null;
    },
  ) => Promise<void>;
  onSaveParrainageBonus: (bonusInscriptionCdf: number) => Promise<void>;
  onSaveParrainageRegle: (
    id: string,
    payload: { seuilFilleuls: number; tauxCommissionPct: number },
  ) => Promise<void>;
};

function SaveRowButton({ isSaving, onClick }: { isSaving: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={isSaving}
      onClick={onClick}
      className="academic-panel-btn academic-panel-btn--primary"
    >
      <LucideSymbolIcon name="save" tone="inherit" size={16} className="text-white" />
      {isSaving ? '…' : 'Enregistrer'}
    </button>
  );
}

function SaveFooterButton({
  isSaving,
  label,
  onClick,
}: {
  isSaving: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={isSaving}
      onClick={onClick}
      className="academic-panel-btn academic-panel-btn--primary"
    >
      <LucideSymbolIcon name="save" tone="inherit" size={18} className="text-white" />
      {isSaving ? 'Enregistrement…' : label}
    </button>
  );
}

function TarifField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={className ?? 'tarif-field'}>
      <span className="tarif-field__label">{label}</span>
      {children}
    </label>
  );
}

function toInputAmount(value: number | string | null | undefined) {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
}

function parseDraftCdf(raw: string) {
  const n = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) {
    throw new Error('Montant CDF invalide.');
  }
  return n;
}

function parseDraftUsd(raw: string) {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) {
    throw new Error('Référence USD invalide.');
  }
  return n;
}

function TarifRowEditor({
  row,
  draft,
  isSaving,
  onDraftChange,
  onSave,
}: {
  row: TarifRow;
  draft: FormationDraft;
  isSaving: boolean;
  onDraftChange: (next: FormationDraft) => void;
  onSave: () => void;
}) {
  return (
    <tr>
      <td className="font-medium text-slate-900">
        {TYPE_FRAIS_LABELS[row.typeFrais] ?? row.typeFrais}
      </td>
      <td>
        <input
          type="number"
          min={0}
          step={1}
          value={draft.montantCdf}
          onChange={(e) => onDraftChange({ ...draft, montantCdf: e.target.value })}
          className={tarifInputClass}
          aria-label={`Montant CDF ${TYPE_FRAIS_LABELS[row.typeFrais]}`}
        />
      </td>
      <td>
        <input
          type="number"
          min={0}
          step={0.01}
          value={draft.referenceUsd}
          onChange={(e) => onDraftChange({ ...draft, referenceUsd: e.target.value })}
          placeholder="—"
          className={tarifInputClass}
          aria-label={`Référence USD ${TYPE_FRAIS_LABELS[row.typeFrais]}`}
        />
      </td>
      <td className="text-right">
        <SaveRowButton isSaving={isSaving} onClick={() => void onSave()} />
      </td>
    </tr>
  );
}

function ParrainageRegleRow({
  regle,
  draft,
  isSaving,
  onDraftChange,
  onSave,
}: {
  regle: ParrainageRegle;
  draft: RegleDraft;
  isSaving: boolean;
  onDraftChange: (next: RegleDraft) => void;
  onSave: () => void;
}) {
  return (
    <tr>
      <td className="font-medium text-slate-900">{regle.typeProfilLabel}</td>
      <td>
        <input
          type="number"
          min={1}
          step={1}
          value={draft.seuilFilleuls}
          onChange={(e) => onDraftChange({ ...draft, seuilFilleuls: e.target.value })}
          className={`${tarifInputClass} max-w-[8rem]`}
        />
      </td>
      <td>
        <div className="flex max-w-[9rem] items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={draft.tauxCommissionPct}
            onChange={(e) => onDraftChange({ ...draft, tauxCommissionPct: e.target.value })}
            className={tarifInputClass}
          />
          <span className="text-sm font-semibold text-on-surface-variant">%</span>
        </div>
      </td>
      <td className="text-right">
        <SaveRowButton isSaving={isSaving} onClick={onSave} />
      </td>
    </tr>
  );
}

export default function AcademiaTarifsPanel({
  items,
  formateur,
  parrainage,
  isLoading,
  isError,
  isSaving,
  toastMessage,
  toastVariant,
  onDismissToast,
  onRefresh,
  onSaveFormation,
  onSaveFormateur,
  onSaveParrainageBonus,
  onSaveParrainageRegle,
}: Props) {
  useRegisterPortalRefresh(onRefresh);
  const [formationDrafts, setFormationDrafts] = useState<Record<string, FormationDraft>>({});
  const [formateurDraft, setFormateurDraft] = useState<FormateurDraft | null>(null);
  const [bonusDraft, setBonusDraft] = useState('');
  const [regleDrafts, setRegleDrafts] = useState<Record<string, RegleDraft>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, FormationDraft> = {};
    for (const row of items) {
      next[row.id] = {
        montantCdf: toInputAmount(row.montantCdf),
        referenceUsd: toInputAmount(row.referenceUsd),
      };
    }
    setFormationDrafts(next);
  }, [items]);

  useEffect(() => {
    setBonusDraft(
      parrainage?.bonusInscriptionCdf != null && Number.isFinite(Number(parrainage.bonusInscriptionCdf))
        ? String(parrainage.bonusInscriptionCdf)
        : '',
    );
    const nextRegles: Record<string, RegleDraft> = {};
    for (const r of parrainage?.regles ?? []) {
      nextRegles[r.id] = {
        seuilFilleuls: String(r.seuilFilleuls ?? 10),
        tauxCommissionPct: String(r.tauxCommissionPct ?? 0),
      };
    }
    setRegleDrafts(nextRegles);
  }, [parrainage]);

  useEffect(() => {
    if (!formateur) {
      setFormateurDraft(null);
      return;
    }
    setFormateurDraft({
      montantHoraireCdf: toInputAmount(formateur.montantHoraireCdf),
      primeTransportJourCdf: toInputAmount(formateur.primeTransportJourCdf),
      referenceUsdHoraire: toInputAmount(formateur.referenceUsdHoraire),
      referenceUsdTransport: toInputAmount(formateur.referenceUsdTransport),
    });
  }, [formateur]);

  const byCategorie = useMemo(() => {
    const map = new Map<string, TarifRow[]>();
    for (const cat of CATEGORIE_ORDER) {
      map.set(cat, []);
    }
    for (const row of items) {
      const list = map.get(row.categorie) ?? [];
      list.push(row);
      map.set(row.categorie, list);
    }
    for (const cat of CATEGORIE_ORDER) {
      const list = map.get(cat) ?? [];
      list.sort(
        (a, b) =>
          TYPE_FRAIS_ORDER.indexOf(a.typeFrais as (typeof TYPE_FRAIS_ORDER)[number]) -
          TYPE_FRAIS_ORDER.indexOf(b.typeFrais as (typeof TYPE_FRAIS_ORDER)[number]),
      );
    }
    return map;
  }, [items]);

  const actifs = items.filter((t) => t.actif !== false).length;
  const categoriesWithRows = CATEGORIE_ORDER.filter((cat) => (byCategorie.get(cat) ?? []).length > 0);

  const sideCard = useMemo(
    () => ({
      label: 'Lignes tarifaires',
      value: isLoading ? '…' : String(actifs),
      helper: isLoading ? null : `${actifs} frais formation · catégories apprenant`,
      sideLinks: [
        { label: 'Devise & taux', to: '/admin/settings', icon: 'settings_suggest' },
        { label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' },
      ],
    }),
    [isLoading, actifs],
  );

  const handleSaveFormation = async (row: TarifRow) => {
    const draft = formationDrafts[row.id];
    if (!draft) return;
    setLocalError(null);
    try {
      await onSaveFormation(row.id, {
        montantCdf: parseDraftCdf(draft.montantCdf),
        referenceUsd: parseDraftUsd(draft.referenceUsd),
      });
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  const handleSaveParrainageBonus = async () => {
    setLocalError(null);
    try {
      await onSaveParrainageBonus(parseDraftCdf(bonusDraft));
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  const handleSaveParrainageRegle = async (regle: ParrainageRegle) => {
    const draft = regleDrafts[regle.id];
    if (!draft) return;
    setLocalError(null);
    try {
      const seuil = Math.floor(Number(draft.seuilFilleuls));
      const taux = Number(String(draft.tauxCommissionPct).replace(',', '.'));
      if (!Number.isFinite(seuil) || seuil < 1) throw new Error('Seuil de filleuls invalide.');
      if (!Number.isFinite(taux) || taux < 0 || taux > 100) {
        throw new Error('Pourcentage invalide (0 à 100).');
      }
      await onSaveParrainageRegle(regle.id, { seuilFilleuls: seuil, tauxCommissionPct: taux });
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  const handleSaveFormateur = async () => {
    if (!formateur?.id || !formateurDraft) return;
    setLocalError(null);
    try {
      await onSaveFormateur(formateur.id, {
        montantHoraireCdf: parseDraftCdf(formateurDraft.montantHoraireCdf),
        primeTransportJourCdf: parseDraftCdf(formateurDraft.primeTransportJourCdf),
        referenceUsdHoraire: parseDraftUsd(formateurDraft.referenceUsdHoraire),
        referenceUsdTransport: parseDraftUsd(formateurDraft.referenceUsdTransport),
      });
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  return (
    <>
      <AdminToast message={toastMessage} variant={toastVariant} onClose={onDismissToast} />
      <AdminPageShell
        title="Tarification des frais"
        sideCard={sideCard}
        description="Gérez les montants des frais (inscription, participation, brevet) et les pourcentages de commission parrainage."
        onRetry={onRefresh}
        isLoading={isLoading}
        isError={isError}
        loadingMessage="Chargement des tarifs…"
        errorMessage="Impossible de charger les tarifs."
      >
        <div className="tarif-page-stack">
          {localError ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {localError}
            </p>
          ) : null}

          <div className="tarif-info-banner">
            <span className="tarif-info-banner__icon" aria-hidden>
              <LucideSymbolIcon name="info" tone="inherit" size={20} />
            </span>
            <p>
              Les frais d&apos;inscription (<strong>INSCRIPTION</strong>) sont facturés à chaque souscription à une
              cohorte, selon la catégorie de l&apos;apprenant. Cette grille s&apos;applique à toutes les formations,
              sauf si une filière définit des <strong>tarifs personnalisés</strong> (Admin → Formations → modifier
              le parcours). Pour recalculer tous les tarifs avec référence USD selon
              le taux du jour, utilisez{' '}
              <Link to="/admin/settings" className="font-bold text-primary hover:underline">
                Devise &amp; taux
              </Link>
              .
            </p>
          </div>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="school" tone="inherit" size={22} />}
            title="Frais de formation et d'inscription"
            subtitle="Montants CDF par catégorie — INSCRIPTION = frais lors de la souscription à un cours."
            variant="accent"
            badge={<span className="academic-panel-badge">{actifs} lignes actives</span>}
          >
            {categoriesWithRows.length === 0 ? (
              <p className="text-sm text-on-surface-variant">Aucun tarif configuré.</p>
            ) : (
              categoriesWithRows.map((categorie) => {
                const rows = byCategorie.get(categorie) ?? [];
                return (
                  <div key={categorie} className="tarif-categorie-block">
                    <div className="tarif-categorie-block__head">
                      <h4 className="tarif-categorie-block__title">
                        {CATEGORIE_LABELS[categorie] ?? categorie}
                      </h4>
                      <span className="tarif-categorie-block__count">{rows.length} type(s) de frais</span>
                    </div>
                    <AcademicPanelTableWrap>
                      <table className="academic-panel-table">
                        <thead>
                          <tr>
                            <th>Type de frais</th>
                            <th>Montant CDF</th>
                            <th>Réf. USD (optionnel)</th>
                            <th className="text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <TarifRowEditor
                              key={row.id}
                              row={row}
                              draft={
                                formationDrafts[row.id] ?? {
                                  montantCdf: toInputAmount(row.montantCdf),
                                  referenceUsd: toInputAmount(row.referenceUsd),
                                }
                              }
                              isSaving={isSaving}
                              onDraftChange={(next) =>
                                setFormationDrafts((prev) => ({ ...prev, [row.id]: next }))
                              }
                              onSave={() => void handleSaveFormation(row)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </AcademicPanelTableWrap>
                  </div>
                );
              })
            )}
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="groups" tone="inherit" size={22} />}
            title="Parrainage"
            subtitle="Commissions % sur les frais de participation intégralement payés — une fois par filleul, seuil = filleuls payeurs."
            variant="preview"
            badge={
              <span className="academic-panel-badge">
                {(parrainage?.regles ?? []).length} profil(s)
              </span>
            }
          >
            <div className="tarif-bonus-panel">
              <div className="tarif-bonus-panel__copy">
                <p className="tarif-bonus-panel__label">
                  <LucideSymbolIcon name="stars" tone="inherit" size={18} className="text-primary" />
                  Bonus à la création de compte
                </p>
                <p className="tarif-bonus-panel__hint">
                  Désactivé pour éviter les comptes fantômes. Le parrain n&apos;est crédité qu&apos;après le seuil de
                  filleuls payeurs, lorsque le filleul a réglé intégralement ses frais de participation (une seule fois
                  par filleul — pas sur les frais d&apos;inscription).
                </p>
              </div>
              <div className="tarif-bonus-panel__field">
                <TarifField label="Bonus (CDF) — inactif">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value="0"
                    disabled
                    readOnly
                    className={`${tarifInputClass} opacity-60`}
                  />
                </TarifField>
              </div>
            </div>

            <p className="tarif-subsection-title mt-6">
              Commissions (% des frais de participation — seuil = filleuls payeurs)
            </p>
            <AcademicPanelTableWrap empty={(parrainage?.regles ?? []).length === 0 ? 'Aucune règle de commission.' : undefined}>
              <table className="academic-panel-table">
                <thead>
                  <tr>
                    <th>Profil du parrain</th>
                      <th>Seuil filleuls payeurs</th>
                    <th>Taux commission</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(parrainage?.regles ?? []).map((regle) => (
                    <ParrainageRegleRow
                      key={regle.id}
                      regle={regle}
                      draft={
                        regleDrafts[regle.id] ?? {
                          seuilFilleuls: String(regle.seuilFilleuls),
                          tauxCommissionPct: String(regle.tauxCommissionPct),
                        }
                      }
                      isSaving={isSaving}
                      onDraftChange={(next) => setRegleDrafts((prev) => ({ ...prev, [regle.id]: next }))}
                      onSave={() => void handleSaveParrainageRegle(regle)}
                    />
                  ))}
                </tbody>
              </table>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>

          {formateur && formateurDraft ? (
            <AcademicPanelCard
              icon={<LucideSymbolIcon name="workspace_premium" tone="inherit" size={22} />}
              title="Rémunération formateurs"
              subtitle="Montants utilisés pour la planification et le suivi des vacations."
              variant="success"
              footer={
                <SaveFooterButton
                  isSaving={isSaving}
                  label="Enregistrer les tarifs formateur"
                  onClick={() => void handleSaveFormateur()}
                />
              }
            >
              <div className="tarif-field-grid">
                <TarifField label="Montant horaire (CDF)">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={formateurDraft.montantHoraireCdf}
                    onChange={(e) =>
                      setFormateurDraft((prev) =>
                        prev ? { ...prev, montantHoraireCdf: e.target.value } : prev,
                      )
                    }
                    className={tarifInputClass}
                  />
                </TarifField>
                <TarifField label="Prime transport / jour (CDF)">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={formateurDraft.primeTransportJourCdf}
                    onChange={(e) =>
                      setFormateurDraft((prev) =>
                        prev ? { ...prev, primeTransportJourCdf: e.target.value } : prev,
                      )
                    }
                    className={tarifInputClass}
                  />
                </TarifField>
                <TarifField label="Réf. USD horaire (optionnel)">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formateurDraft.referenceUsdHoraire}
                    onChange={(e) =>
                      setFormateurDraft((prev) =>
                        prev ? { ...prev, referenceUsdHoraire: e.target.value } : prev,
                      )
                    }
                    className={tarifInputClass}
                    placeholder="—"
                  />
                </TarifField>
                <TarifField label="Réf. USD transport (optionnel)">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formateurDraft.referenceUsdTransport}
                    onChange={(e) =>
                      setFormateurDraft((prev) =>
                        prev ? { ...prev, referenceUsdTransport: e.target.value } : prev,
                      )
                    }
                    className={tarifInputClass}
                    placeholder="—"
                  />
                </TarifField>
              </div>
            </AcademicPanelCard>
          ) : null}
        </div>
      </AdminPageShell>
    </>
  );
}
